import React, { useState } from 'react';
import { Package, Truck, DollarSign, MapPin, CheckCircle2, Phone, Mail, ArrowRight, Scale, Globe } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';

const TwentyThreeKgParcelPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', numParcels: '' });
  const { toast } = useToast();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ title: "Quote Request Received!", description: "Our team will contact you within 24 hours with 23kg parcel shipping rates to India." });
    setFormData({ name: '', email: '', phone: '', numParcels: '' });
  };

  const benefits = [
    { icon: DollarSign, title: "Affordable 23kg Shipping", description: "Competitive rates for standard 23kg parcels from Canada to India, often cheaper than airline baggage fees." },
    { icon: Package, title: "Perfect Parcel Size", description: "23kg is the standard airline weight limit, making it easy to pack and ship familiar-sized parcels." },
    { icon: Truck, title: "Multiple Parcel Discounts", description: "Shipping 2, 3, or more 23kg parcels? We offer bulk discounts for multiple parcel shipments." },
    { icon: Globe, title: "Nationwide Delivery", description: "Door-to-door delivery from anywhere in Canada to any destination across India." },
    { icon: MapPin, title: "Track Every Parcel", description: "Real-time tracking for each 23kg parcel from pickup in Canada to delivery in India." },
    { icon: CheckCircle2, title: "Simple Process", description: "Easy booking, convenient pickup, professional packaging assistance, and reliable delivery." }
  ];

  const faqs = [
    { question: "How much does it cost to ship a 23kg parcel to India?", answer: "Shipping costs for a 23kg parcel depend on dimensions and destination. Typical rates range from $100-$200 via economy service. For bulk shipments over 100kg total, rates can be from $7.99/kg. Contact us for an exact quote." },
    { question: "Why is 23kg a common parcel weight?", answer: "23kg is the standard checked baggage weight limit for most airlines. Many people are familiar with packing 23kg suitcases, making it a convenient parcel size for international shipping." },
    { question: "Can I ship multiple 23kg parcels together?", answer: "Yes! We offer discounted rates for multiple 23kg parcels. Shipping 2, 3, or more parcels together qualifies for bulk pricing, saving you money compared to individual shipments." },
    { question: "How long does 23kg parcel delivery to India take?", answer: "Express shipping takes 5-10 business days, economy shipping takes 15-25 days. Transit time depends on your origin city in Canada, destination in India, and carrier service selected." },
    { question: "What can I pack in a 23kg parcel?", answer: "You can pack clothing, shoes, electronics, books, household items, gifts, and personal belongings. Prohibited items include liquids, flammables, perishables, and restricted electronics." },
    { question: "Is dimensional weight a factor for 23kg parcels?", answer: "Yes, carriers calculate both actual weight (23kg) and dimensional weight (Length × Width × Height ÷ 5000). The higher value is used for pricing. We help optimize packaging to minimize dimensional weight charges." },
    { question: "Do you provide packaging for 23kg parcels?", answer: "We can assist with packaging recommendations and materials. Most customers use sturdy boxes or hard-sided suitcases for 23kg shipments. Contact us for packaging guidance." },
    { question: "Can students ship 23kg parcels home to India?", answer: "Absolutely! Many international students ship 23kg parcels home during breaks or after graduation. We offer student-friendly rates and campus pickup services." }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>23kg Parcel Shipping Canada to India | Affordable Rates – Shippzy</title>
        <meta name="description" content="Ship 23kg parcels from Canada to India. Affordable rates, door-to-door delivery, cheaper than airline baggage fees. Multiple parcel discounts available. Get instant quote." />
        <meta name="keywords" content="23kg parcel Canada to India, ship 23kg to India, 23 kg courier India, standard parcel shipping India, 23 kilogram package India" />
        <link rel="canonical" href="https://www.shippzy.ca/23kg-parcel-canada-to-india" />
        <meta property="og:title" content="23kg Parcel Shipping to India | Shippzy" />
        <meta property="og:description" content="Affordable 23kg parcel shipping from Canada to India. Bulk discounts, door-to-door service, real-time tracking." />
        <meta property="og:url" content="https://www.shippzy.ca/23kg-parcel-canada-to-india" />
      </Helmet>

      <div className="min-h-screen bg-white">
        <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
                📦 Standard 23kg Parcel Shipping
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">Ship 23kg Parcels from Canada to India</h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                Shippzy specializes in shipping 23kg parcels from Canada to India at competitive rates. Whether you're sending one or multiple 23kg parcels, we provide affordable shipping options through DHL, FedEx, and UPS. Perfect for personal belongings, gifts, student shipments, and relocations. Door-to-door service with customs assistance and real-time tracking included.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-6 text-lg" onClick={() => document.getElementById('quote-form').scrollIntoView({ behavior: 'smooth' })}>Get Instant Quote</Button>
                <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg" onClick={() => window.location.href = 'tel:+14376160758'}>Call: +1 (437) 616-0758</Button>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Ship 23kg Parcels with Shippzy</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">Affordable, reliable, and convenient 23kg parcel shipping to India</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <CardContent className="pt-6">
                      <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">23kg Parcel Shipping Options</h2>
              <p className="text-xl text-gray-600">Flexible solutions for different needs</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: 'Single 23kg Parcel', desc: 'Competitive rates for individual 23kg parcels with express or economy delivery options.' },
                { title: 'Multiple 23kg Parcels', desc: 'Bulk discounts when shipping 2, 3, or more 23kg parcels to the same address in India.' },
                { title: 'Student & Relocation', desc: 'Special rates for students and families relocating, shipping multiple 23kg parcels home.' }
              ].map((item, i) => (
                <Card key={i} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Scale className="w-10 h-10 text-red-600 mb-4" />
                    <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Common questions about 23kg parcel shipping to India</p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="border-2 hover:border-red-300 transition-colors cursor-pointer" onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <ArrowRight className={`w-5 h-5 text-red-600 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {openFaq === index && <p className="text-gray-600 mt-4 leading-relaxed">{faq.answer}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section id="quote-form" className="py-20 bg-gradient-to-r from-red-600 to-yellow-500">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center text-white mb-8">
              <h2 className="text-4xl font-bold mb-4">Ready to Ship Your 23kg Parcels?</h2>
              <p className="text-xl">Get your free shipping quote today</p>
            </div>
            <Card className="shadow-2xl">
              <CardHeader>
                <CardTitle className="text-2xl text-center">Get Your Free Quote</CardTitle>
                <CardDescription className="text-center">Best rates for 23kg parcel shipping to India</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label><Input name="name" value={formData.name} onChange={handleChange} required placeholder="Your name" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label><Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="your@email.com" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label><Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+1 (123) 456-7890" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-2">Number of 23kg Parcels</label><Input type="number" name="numParcels" value={formData.numParcels} onChange={handleChange} placeholder="1, 2, 3..." /></div>
                  <Button type="submit" className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white h-12 text-lg">Get Quote Now</Button>
                </form>
              </CardContent>
            </Card>
            <div className="mt-8 text-center text-white">
              <p className="text-lg mb-4">Or contact us directly:</p>
              <div className="flex flex-col md:flex-row gap-4 justify-center">
                <a href="tel:+14376160758" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"><Phone className="w-5 h-5" />+1 (437) 616-0758</a>
                <a href="mailto:ship@shippzy.ca" className="flex items-center justify-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"><Mail className="w-5 h-5" />ship@shippzy.ca</a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gray-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Affordable 23kg Parcel Shipping to India</h3>
            <p className="text-gray-300 mb-6">Ship standard 23kg parcels from Canada to India at competitive rates. Door-to-door delivery, customs support, bulk discounts available.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white px-8 py-3" onClick={() => window.location.href = '/ship-suitcase-canada-to-india'}>Suitcase Shipping</Button>
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3" onClick={() => window.location.href = '/student-shipping-canada-to-india'}>Student Shipping</Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default TwentyThreeKgParcelPage;
